import type { SuggestedTypeField } from '../shared/desktop-api';

/**
 * The fields a new-type card proposes (D10, docs/planning/autopilot-never-interrupts.md), and which the person keeps.
 * Every proposed field is kept until it is dropped; none kept adds the type with no fields.
 */
export const keptSuggestedFieldNames = (
  proposed: SuggestedTypeField[] | null | undefined,
  dropped: readonly string[],
): string[] =>
  (proposed ?? []).map((field) => field.name).filter((name) => !dropped.includes(name));

/**
 * What the review says about the proposal: none yet, none found, or how many of how many are kept. "None yet" claims no
 * design is running: it may be, or it may never run on this computer (no reader, the setup reasoner off, its attempts
 * used up, no fully read document).
 */
export function suggestedFieldsStatus(
  proposed: SuggestedTypeField[] | null | undefined,
  kept: number,
  autopilot: boolean,
): string {
  if (proposed == null) return 'No fields proposed yet. You can apply now and add fields later.';
  if (proposed.length === 0)
    return autopilot
      ? 'No fields found. Its documents go out without values until you add some.'
      : 'No fields found. You can add them in Job setup.';
  if (kept === 0)
    return autopilot
      ? 'No fields kept. Its documents go out without values until you add some.'
      : 'No fields kept. You can add them in Job setup.';
  return `${kept} of ${proposed.length} fields kept. Its documents are read for them before they go on.`;
}

/**
 * The type an Apply added: one the job did not have before (by id, so a name the engine matched to an existing type
 * adds none) that carries the applied name, compared without case as the engine compares, so a type another window
 * added meanwhile is never taken for it.
 */
export function addedSuggestedTypeId(
  before: readonly { id: string }[],
  after: readonly { id: string; name: string }[],
  appliedName: string | null | undefined,
): string | undefined {
  const name = appliedName?.trim().toLowerCase();
  if (!name) return undefined;
  return after.find(
    (type) =>
      !before.some((earlier) => earlier.id === type.id) && type.name.trim().toLowerCase() === name,
  )?.id;
}

/** The receipt line once a type was added with fields: which ones, briefly. */
export function newTypeFieldsReceipt(names: readonly string[]): string {
  const shown = names.slice(0, 3).join(', ');
  const more = names.length > 3 ? ` and ${names.length - 3} more` : '';
  return `It reads ${shown}${more}. Its documents are read for them before they go on.`;
}
